import type {
  AnalitoResponse,
  ReconciliarAnalitosDtoAnalitosItem,
  ReferenciaResponse,
} from '@/api/generated/model';
import type { Especie } from '@/features/admin/catalogo-exames/rotulos';

/**
 * O RASCUNHO da grade — o estado que o admin edita antes de salvar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Por que um rascunho, e não edição direta com uma requisição por campo:
 *  a grade mexe em 3 analitos × 5 espécies de uma vez. Salvar célula a célula
 *  seriam 15 requisições que podem falhar na oitava, deixando o catálogo num
 *  estado que ninguém escolheu. Aqui o admin edita à vontade e o `PUT` é um só.
 *
 *  Tudo é STRING no rascunho, inclusive mínimo e máximo. É o que o `<input>`
 *  devolve, e é o que permite o campo ficar vazio (que não é zero) ou conter
 *  "3,5" no meio da digitação. A conversão para número acontece uma vez, ao
 *  montar o payload.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface FaixaRascunho {
  /** Ausente = faixa nova, ainda não gravada. */
  id?: string;
  especie: Especie;
  sexo: string | null;
  idadeMinMeses: number | null;
  idadeMaxMeses: number | null;
  minimo: string;
  maximo: string;
  textoReferencia: string;
  observacao: string;
}

export interface AnalitoRascunho {
  /** Ausente = analito novo. */
  id?: string;
  nome: string;
  unidade: string;
  apelidosVertis: string[];
  ativo: boolean;
  faixas: FaixaRascunho[];
}

/** Aceita vírgula: o admin digita "3,5", não "3.5". */
export function paraNumero(texto: string): number | null {
  const limpo = texto.trim().replace(',', '.');
  if (!limpo) return null;
  const valor = Number(limpo);
  return Number.isFinite(valor) ? valor : null;
}

function paraTexto(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  // Vírgula na exibição, como o veterinário brasileiro lê.
  return String(valor).replace('.', ',');
}

export function faixaParaRascunho(faixa: ReferenciaResponse): FaixaRascunho {
  return {
    id: faixa.id,
    especie: faixa.especie as Especie,
    sexo: faixa.sexo,
    idadeMinMeses: faixa.idadeMinMeses,
    idadeMaxMeses: faixa.idadeMaxMeses,
    minimo: paraTexto(faixa.minimo),
    maximo: paraTexto(faixa.maximo),
    textoReferencia: faixa.textoReferencia ?? '',
    observacao: faixa.observacao ?? '',
  };
}

export function paraRascunho(analitos: readonly AnalitoResponse[]): AnalitoRascunho[] {
  return analitos.map((analito) => ({
    id: analito.id,
    nome: analito.nome,
    unidade: analito.unidade ?? '',
    apelidosVertis: [...analito.apelidosVertis],
    ativo: analito.ativo,
    faixas: analito.referencias.map(faixaParaRascunho),
  }));
}

/**
 * A faixa cabe numa célula da grade?
 *
 * Só a faixa SIMPLES (numérica, sem sexo e sem idade) é editável inline — ela é
 * a esmagadora maioria. Faixa por sexo/idade ou qualitativa continua no diálogo,
 * que tem espaço para todos os campos; espremê-la numa célula esconderia
 * justamente o que a torna diferente.
 */
export function cabeNaCelula(faixa: FaixaRascunho): boolean {
  return (
    faixa.sexo === null &&
    faixa.idadeMinMeses === null &&
    faixa.idadeMaxMeses === null &&
    faixa.textoReferencia.trim() === ''
  );
}

export function faixasDaEspecie(analito: AnalitoRascunho, especie: Especie): FaixaRascunho[] {
  return analito.faixas.filter((f) => f.especie === especie);
}

/** Espécies que já têm alguma faixa, na ordem canônica dos rótulos. */
export function especiesEmUso(
  analitos: readonly AnalitoRascunho[],
  ordemCanonica: readonly Especie[],
): Especie[] {
  const presentes = new Set(analitos.flatMap((a) => a.faixas.map((f) => f.especie)));
  return ordemCanonica.filter((e) => presentes.has(e));
}

/**
 * Monta o corpo do `PUT`.
 *
 * ⚠️ Célula VAZIA não vira faixa. É o que permite o admin abrir a grade, digitar
 * só o canino e salvar sem criar cinco faixas em branco — que o servidor
 * recusaria (`Informe mínimo/máximo ou um texto de referência`) e que, se
 * aceitasse, poluiriam o laudo com faixas sem valor.
 */
export function paraPayload(
  analitos: readonly AnalitoRascunho[],
): ReconciliarAnalitosDtoAnalitosItem[] {
  return analitos.map((analito, ordem) => ({
    ...(analito.id ? { id: analito.id } : {}),
    nome: analito.nome.trim(),
    unidade: analito.unidade.trim() ? analito.unidade.trim() : null,
    ordem,
    ativo: analito.ativo,
    apelidosVertis: analito.apelidosVertis,
    referencias: analito.faixas
      .filter((faixa) => {
        const temNumero = paraNumero(faixa.minimo) !== null || paraNumero(faixa.maximo) !== null;
        return temNumero || faixa.textoReferencia.trim() !== '';
      })
      .map((faixa) => ({
        ...(faixa.id ? { id: faixa.id } : {}),
        especie: faixa.especie,
        sexo: (faixa.sexo ?? null) as never,
        idadeMinMeses: faixa.idadeMinMeses,
        idadeMaxMeses: faixa.idadeMaxMeses,
        minimo: paraNumero(faixa.minimo),
        maximo: paraNumero(faixa.maximo),
        textoReferencia: faixa.textoReferencia.trim() || null,
        observacao: faixa.observacao.trim() || null,
      })),
  }));
}

/**
 * Modelos de ESTRUTURA — quais analitos o exame tem, nunca com que valores.
 *
 * ⚠️ Não existe modelo com faixa preenchida, e é decisão de segurança clínica:
 * faixa errada é pior que faixa vazia. Uma faixa ausente sai `INDETERMINADO`
 * ("não sei"); uma faixa chutada diz "normal" sobre um valor alterado.
 *
 * Os nomes são os do Vertis porque é por eles que o sync casa o resultado
 * (`modules/sync/lib/casamento.ts` no backend). Trocá-los por nomes bonitos
 * faria o laudo chegar sem a linha.
 */
export const MODELOS: readonly { rotulo: string; analitos: string[] }[] = [
  { rotulo: 'Basal e pós-ACTH', analitos: ['RESULTADO BASAL', 'RESULTADO POS ACTH'] },
  { rotulo: 'Supressão T0h / T8h', analitos: ['RESULTADO T0', 'RESULTADO (T8h pós DXM)'] },
  { rotulo: 'Supressão T0h / T4h / T8h', analitos: ['RESULTADO T0h', 'RESULTADO T4h', 'RESULTADO T8h'] },
  { rotulo: 'Pré e pós-medicamento', analitos: ['T4 BASAL', 'T4  POS  MEDICAMENTO'] },
  { rotulo: 'Duas coletas', analitos: ['RESULTADO  COLETA 1', 'RESULTADO COLETA 2'] },
];

/** Acrescenta só o que falta — aplicar duas vezes não duplica nem apaga. */
export function aplicarModelo(
  analitos: readonly AnalitoRascunho[],
  nomes: readonly string[],
): AnalitoRascunho[] {
  const existentes = new Set(analitos.map((a) => a.nome.trim().toLocaleUpperCase('pt-BR')));
  const novos = nomes
    .filter((nome) => !existentes.has(nome.toLocaleUpperCase('pt-BR')))
    .map((nome) => ({ nome, unidade: '', apelidosVertis: [], ativo: true, faixas: [] }));

  return [...analitos, ...novos];
}
